import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, Wallet, CheckCircle2, AlertCircle, Eye, EyeOff, PiggyBank, ReceiptText, FileBarChart2 } from "lucide-react";
import { Link } from "react-router-dom";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import api, { formatRupiah, formatTanggal, BULAN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

function StatCard({ icon: Icon, label, value, sub, tone, testId }) {
  const tones = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    rose: "bg-rose-50 text-rose-600",
    slate: "bg-slate-100 text-slate-600",
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="rounded-xl border border-slate-200 bg-white p-6"
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${tones[tone]}`}>
          <Icon className="h-5 w-5" strokeWidth={1.5} />
        </div>
      </div>
      <p className="mt-3 font-heading text-2xl font-bold tracking-tight text-slate-900">{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </motion.div>
  );
}

export default function Dashboard() {
  const { schoolId, user } = useAuth();
  const [data, setData] = useState(null);
  const [needsSchool, setNeedsSchool] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [chartReady, setChartReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setChartReady(true), 400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (user.role === "super_admin" && !schoolId) {
      setNeedsSchool(true);
      setData(null);
      return;
    }
    let cancelled = false;
    setNeedsSchool(false);
    setData(null);
    api.get("/dashboard/summary")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (!cancelled && e.response?.status === 400) setNeedsSchool(true);
        if (!cancelled && e.response?.status !== 400) setData({ error: true });
      });
    return () => { cancelled = true; };
  }, [schoolId]);

  if (needsSchool) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="dashboard-need-school">
        <p className="font-heading text-lg font-semibold text-slate-800">Pilih sekolah terlebih dahulu</p>
        <p className="mt-1 text-sm text-slate-500">Gunakan pemilih sekolah di pojok kiri atas untuk melihat dashboard.</p>
      </div>
    );
  }

  if (!data) {
    return <div className="py-20 text-center text-sm text-slate-500" data-testid="dashboard-loading">Memuat dashboard...</div>;
  }

  if (data.error) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-12 text-center" data-testid="dashboard-error">
        <p className="font-heading text-lg font-semibold text-rose-700">Gagal memuat dashboard</p>
        <p className="mt-1 text-sm text-rose-500">Terjadi kesalahan pada server. Silakan muat ulang halaman.</p>
      </div>
    );
  }

  const pct = data.total_students > 0 ? Math.round((data.paid_count / data.total_students) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#0060AF] to-[#004B87] p-6 text-white shadow-md sm:p-8"
        data-testid="dashboard-hero"
      >
        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-blue-200">Pemasukan Bulan Ini · {data.month_label}</p>
        <div className="mt-3 flex items-center gap-3">
          <h1 className="font-heading text-4xl font-bold tracking-tighter tabular-nums sm:text-5xl" data-testid="hero-pemasukan">
            {hidden ? "Rp ••••••••" : formatRupiah(data.income_this_month)}
          </h1>
          <button
            onClick={() => setHidden(!hidden)}
            data-testid="hero-toggle-balance"
            className="rounded-full p-2 text-blue-200 transition-colors hover:bg-white/10 hover:text-white"
          >
            {hidden ? <EyeOff className="h-5 w-5" strokeWidth={1.5} /> : <Eye className="h-5 w-5" strokeWidth={1.5} />}
          </button>
        </div>
        <p className="mt-3 text-sm text-blue-100">
          {data.school_name} · SPP {formatRupiah(data.income_spp)} + Tagihan {formatRupiah(data.income_tagihan)} · Saldo tabungan siswa: <span className="font-semibold text-white tabular-nums">{hidden ? "Rp ••••••" : formatRupiah(data.savings_balance)}</span>
        </p>
      </motion.div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="quick-actions">
        {[
          { to: "/spp", label: "Bayar SPP", icon: Wallet, testId: "qa-spp" },
          { to: "/tabungan", label: "Tabungan", icon: PiggyBank, testId: "qa-tabungan" },
          { to: "/tagihan", label: "Tagihan", icon: ReceiptText, testId: "qa-tagihan" },
          { to: "/laporan", label: "Laporan", icon: FileBarChart2, testId: "qa-laporan" },
        ].map((a) => (
          <Link
            key={a.to}
            to={a.to}
            data-testid={a.testId}
            className="flex flex-col items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:border-[#0060AF]/40 hover:bg-[#E5F0FA]/50"
          >
            <a.icon className="h-6 w-6 text-[#0060AF]" strokeWidth={1.5} />
            {a.label}
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <StatCard icon={Users} label="Total Siswa" value={data.total_students} sub="Siswa aktif" tone="slate" testId="stat-total-siswa" />
        <StatCard icon={CheckCircle2} label="Sudah Lunas" value={`${data.paid_count} siswa`} sub={`${pct}% lunas${data.sebagian_count ? ` · ${data.sebagian_count} cicilan` : ""}`} tone="emerald" testId="stat-lunas" />
        <StatCard icon={AlertCircle} label="Belum Bayar" value={`${data.unpaid_count} siswa`} sub="Perlu ditindaklanjuti" tone="rose" testId="stat-belum-bayar" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="rounded-xl border border-slate-200 bg-white p-6 lg:col-span-2"
        >
          <h3 className="font-heading text-base font-semibold text-slate-800">Tren Pemasukan SPP (6 Bulan)</h3>
          <div className="mt-4 h-64" data-testid="chart-tren-pembayaran">
            {chartReady && (
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={240}>
              <AreaChart data={data.trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563EB" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 11, fill: "#64748B" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => (v >= 1000000 ? `${(v / 1000000).toFixed(1)}jt` : v >= 1000 ? `${v / 1000}rb` : v)}
                />
                <Tooltip formatter={(v) => formatRupiah(v)} labelStyle={{ color: "#0F172A" }} />
                <Area type="linear" dataKey="total" stroke="#2563EB" strokeWidth={2} fill="url(#colorTotal)" name="Pemasukan" />
              </AreaChart>
            </ResponsiveContainer>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
          className="rounded-xl border border-slate-200 bg-white p-6"
        >
          <h3 className="font-heading text-base font-semibold text-slate-800">Belum Bayar Bulan Ini</h3>
          <div className="mt-4 space-y-3" data-testid="list-belum-bayar">
            {data.unpaid_students.length === 0 && (
              <p className="py-6 text-center text-sm text-slate-500">
                {data.total_students === 0
                  ? "Belum ada siswa terdaftar di sekolah ini."
                  : <span className="text-emerald-600">Semua siswa sudah lunas. Bagus!</span>}
              </p>
            )}
            {data.unpaid_students.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-slate-800">{s.name}</p>
                  <p className="text-xs text-slate-500">{s.class_name} · {s.nisn}</p>
                </div>
                {s.total_paid > 0 ? (
                  <Badge className="rounded-full bg-amber-50 text-amber-600 hover:bg-amber-50">Sebagian {s.percent}%</Badge>
                ) : (
                  <Badge className="rounded-full bg-rose-50 text-rose-600 hover:bg-rose-50">Belum</Badge>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        className="rounded-xl border border-slate-200 bg-white p-6"
      >
        <h3 className="font-heading text-base font-semibold text-slate-800">Pembayaran Terbaru</h3>
        <div className="mt-4 overflow-x-auto">
          <Table data-testid="table-pembayaran-terbaru">
            <TableHeader>
              <TableRow>
                <TableHead>Siswa</TableHead>
                <TableHead>Periode</TableHead>
                <TableHead>Metode</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead className="text-right">Jumlah</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.recent_payments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-sm text-slate-500">
                    Belum ada pembayaran tercatat.
                  </TableCell>
                </TableRow>
              )}
              {data.recent_payments.map((p) => (
                <TableRow key={p.id} className="transition-colors duration-200 hover:bg-slate-50">
                  <TableCell>
                    <p className="text-sm font-medium text-slate-800">{p.student_name}</p>
                    <p className="text-xs text-slate-500">{p.class_name}</p>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{BULAN[p.month - 1]} {p.year}</TableCell>
                  <TableCell className="text-sm text-slate-600">{p.method}</TableCell>
                  <TableCell className="text-sm text-slate-600">{formatTanggal(p.paid_at)}</TableCell>
                  <TableCell className="text-right font-medium text-slate-900">{formatRupiah(p.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </motion.div>
    </div>
  );
}
